import { usePrintActionHandler } from '@nest/internal-frontend';
import { Button, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger, useValidationResultDialog } from '@nest/uikit-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';
import { useTerminalClient } from '../client-rpc-router';
import { TerminalScreen } from '../components/TerminalScreen';
import { useNumberParam } from '../helpers';

export default function DeliveryProcessPage() {
	const shipmentRouteId = useNumberParam('shipmentRouteId');
	const warehouseRelationId = useNumberParam('warehouseRelationId');
	const client = useTerminalClient();
	const navigate = useNavigate();
	const [bottomElementRef, setBottomElementRef] = useState<HTMLDivElement | null>(null);
	const [activeTab, setActiveTab] = useState<'remaining' | 'loaded'>('remaining');
	const printActionHandler = usePrintActionHandler(
		'outboundDocumentPrintInvoicesByShipmentRouteTerminal',
		(numberOfCopies, printerIds) => {
			return client.outboundDocuments.schedulePrintInvoiceByShipmentRouteIdQuery.execute({
				shipmentRouteId: shipmentRouteId!,
				numberOfCopies,
				printerIds
			});
		},
		{
			allowedActions: ['print']
		}
	);

	const { data: route } = useQuery({
		queryKey: ['shipment-route', shipmentRouteId],
		queryFn: () => client.shipmentRoutes.getById.execute({ id: shipmentRouteId!, populate: ['vehicle'] }),
		enabled: !!shipmentRouteId
	});

	const { data: itemsToLoad } = useQuery({
		queryKey: ['shipment-route-items-to-load', shipmentRouteId],
		queryFn: () => client.shipments.getShipmentRouteItemsToLoadQuery.execute({ shipmentRouteId: shipmentRouteId! }),
		enabled: !!shipmentRouteId
	});

	const { data: relation } = useQuery({
		queryKey: ['shipment-route-relation', warehouseRelationId],
		queryFn: () => client.warehouseRelations.getById.execute({ id: warehouseRelationId!, populate: ['items', 'items.stockItem', 'items.stockItem.item'] }),
		enabled: !!warehouseRelationId
	});

	const { data: remaining } = useQuery({
		queryKey: ['shipment-route-remaining-items', shipmentRouteId],
		queryFn: () => client.shipments.getShipmentRouteRemainingItemsToPickQuery.execute({ shipmentRouteIds: [shipmentRouteId!] }),
		enabled: !!shipmentRouteId
	});

	const remainingMap = useMemo(() => new Map((remaining?.[0]?.missingItems ?? []).map(x => [x.stockItem!.id, x.quantityToPick])), [remaining]);
	const loadedItemsCount = (relation?.items ?? []).reduce((acc, x) => acc + (x.quantity ?? 0), 0);
	const itemsToLoadCount = (itemsToLoad ?? []).reduce((acc, x) => acc + (x.quantityToLoad ?? 0), 0);

	const dateText = route?.startAt
		? new Intl.DateTimeFormat('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(route.startAt))
		: '';

	const missingCount = useMemo(() => remainingMap.size, [remainingMap]);

	const getMissingCountColor = useMemo(
		() => () => {
			if (remaining == null) {
				return 'bg-muted';
			}
			return missingCount > 0 ? 'bg-red-600' : 'bg-green-600';
		},
		[remaining, missingCount]
	);

	const confirmRouteLoadingMutation = useMutation({
		mutationFn: () => client.shipments.confirmShipmentRouteLoadingMutation.execute({ shipmentRouteId: shipmentRouteId! }),
		onSuccess: () => {
			printActionHandler.run().then(() => navigate('/delivery'));
		}
	});

	useValidationResultDialog([confirmRouteLoadingMutation.error]);

	const confirmRouteLoading = () => {
		return confirmRouteLoadingMutation.mutateAsync();
	};

	return (
		<TerminalScreen
			showBackButton
			topBar={
				<div className="flex items-center gap-2">
					<div className={`h-2 w-2 rounded-sm ${getMissingCountColor()}`} />
					<div>
						{route?.vehicle?.name ?? ''}
						<div className="text-xs text-muted-foreground">{dateText}</div>
					</div>
				</div>
			}
			bottomContent={<div ref={setBottomElementRef} />}
		>
			<div className="h-full flex flex-col">
				<Tabs value={activeTab} onValueChange={v => setActiveTab(v)} className="flex-1">
					<TabsContent value="remaining" className="space-y-2 p-2">
						{itemsToLoad ? (
							<>
								{itemsToLoad.length === 0 && <div className="p-6 text-center text-muted-foreground">Žádné položky k naložení</div>}
								{itemsToLoad[0] && (
									<div>
										<div className="mb-1">První v pořadí</div>
										<div
											className="bg-white border rounded p-2 flex flex-col gap-1"
											onClick={() =>
												navigate(
													`/delivery/list/${shipmentRouteId}/process/${warehouseRelationId}/stops/${itemsToLoad[0]!.shipmentStopId}/stocks/${itemsToLoad[0]!.stockItem!.id}`
												)
											}
										>
											<div className="font-semibold">{itemsToLoad[0].stockItem!.item.code}</div>
											<div>{itemsToLoad[0].stockItem!.item.name}</div>
											<div className="flex justify-between">
												<div
													className={`${(remainingMap.get(itemsToLoad[0].stockItem!.id) ?? 0) > 0 ? 'bg-red-100 text-red-800 px-2 rounded-md' : 'opacity-0'}`}
												>
													Vyskladnit: {remainingMap.get(itemsToLoad[0].stockItem!.id) ?? 0}ks
												</div>
												<div>Naložit: {itemsToLoad[0].quantityToLoad ?? 0}ks</div>
											</div>
										</div>
									</div>
								)}
								{itemsToLoad.slice(1).length > 0 && (
									<div className="opacity-50 gap-2 flex flex-col">
										<div className="mt-2">Další v pořadí</div>
										{itemsToLoad.slice(1).map(item => (
											<div key={`${item.stockItem!.id}`} className="bg-white border rounded p-2 flex flex-col gap-1">
												<div className="font-semibold">{item.stockItem!.item.code}</div>
												<div>{item.stockItem!.item.name}</div>
												<div className="flex justify-between">
													<div className={`${(remainingMap.get(item.stockItem!.id) ?? 0) > 0 ? 'bg-red-100 text-red-800 px-2 rounded-md' : 'opacity-0'}`}>
														Vyskladnit: {remainingMap.get(item.stockItem!.id) ?? 0}ks
													</div>
													<div>Naložit: {item.quantityToLoad ?? 0}ks</div>
												</div>
											</div>
										))}
									</div>
								)}
							</>
						) : (
							<div className="space-y-2">
								<Skeleton className="h-20 w-full" />
								<Skeleton className="h-20 w-full" />
							</div>
						)}
					</TabsContent>

					<TabsContent value="loaded" className="space-y-2 p-2">
						{(relation?.items ?? []).length === 0 ? (
							<div className="p-6 text-center text-muted-foreground">Žádné položky nebyly naloženy</div>
						) : (
							(relation?.items ?? []).map(item => (
								<div key={`${item.stockItem!.id}`} className="bg-white border rounded p-2 flex flex-row items-center gap-4">
									<div>
										<div className="font-semibold">{item.stockItem!.item.code}</div>
										<div>{item.stockItem!.item.name}</div>
									</div>
									<div>
										<div className="text-sm text-nowrap">naloženo</div>
										<div className="h-10 px-2 bg-slate-200 font-bold rounded-md flex items-center justify-center text-nowrap">{item.quantity}</div>
									</div>
								</div>
							))
						)}
						<div className="flex-shrink-0 border-t pt-2 sticky bottom-0">
							<Button className="w-full" onClick={confirmRouteLoading}>
								Potvrdit vyložení
							</Button>
						</div>
					</TabsContent>

					{bottomElementRef &&
						createPortal(
							<TabsList className="grid grid-cols-2 w-full">
								<TabsTrigger value="remaining" className="w-full">
									Nezpracované ({itemsToLoadCount})
								</TabsTrigger>
								<TabsTrigger value="loaded" className="w-full">
									Naložené ({loadedItemsCount})
								</TabsTrigger>
							</TabsList>,
							bottomElementRef
						)}
				</Tabs>
			</div>
		</TerminalScreen>
	);
}
